import type { EndpointBuilder } from '@reduxjs/toolkit/query'

import { rootApi } from 'root-api-graphql'
import { buildFilterString } from 'services/search_api/utils'

import { MEETINGS_TAG_TYPES, buildMeetingListTag, buildMeetingTag } from './constants'
import { getSupergraphBaseUrl } from './supergraphql_api'
import type {
  GraphQLMeetingResult,
  MeetingsGraphQLResult,
  MeetingsSearchResult
} from './types'
import { mapScopeTypeToBackendValue } from './utils'

/** Maps frontend scope (e.g. user/me) to backend values for buildFilterString; omits scopeId when "me". */
function applyScopeMapping(variables: any): any {
  const out = { ...variables }
  const rawScopeType = variables.scopeType ?? variables.scope_type
  const rawScopeId = variables.scopeId ?? variables.scope_id
  const mapped = rawScopeType ? mapScopeTypeToBackendValue(rawScopeType, rawScopeId) : null
  if (mapped) {
    out.scopeType = mapped
    if (rawScopeId === 'me' || mapped === 'my_calendly') {
      delete out.scopeId
      delete out.scope_id
    }
  }
  return out
}

// istanbul ignore next
const GetMeetingsDocument = `
  query getMeetings(
    $query: String!,
    $status: MeetingStatus,
    $statuses: [MeetingStatus!],
    $searchIn: [MeetingSearchInFields!],
    $startTimeRange: DateFilter,
    $userUuids: [String!],
    $inviteeEmails: [String!],
    $eventTypeUuids: [String!],
    $packageUuids: [ID!],
    $kinds: [MeetingKind!],
    $meetingUuid: ID,
    $role: [MeetingRole],
    $orderBy: MeetingsSearchSort,
    $findBestCharForPassages: Boolean,
    $isOrganizer: Boolean,
    $includeNotetakerIneligibleExternalEvents: Boolean,
  ) {
    meetings_search(
      input: {
        query: $query,
        searchIn: $searchIn,
        findBestCharForPassages: $findBestCharForPassages,
        orderBy: $orderBy,
        filter: {
          start_time: $startTimeRange,
          user_uuids: $userUuids,
          invitee_emails: $inviteeEmails,
          event_type_uuids: $eventTypeUuids,
          package_uuids: $packageUuids,
          kinds: $kinds,
          status: $status,
          statuses: $statuses,
          uuid: $meetingUuid,
          role: $role,
          is_organizer: $isOrganizer,
          include_notetaker_ineligible_external_events: $includeNotetakerIneligibleExternalEvents,
        }
      }
    ) {
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
      edges {
        node {
          uuid
          owner_user_uuid
          name
          startTime: start_time
          endTime: end_time
          highlight {
            attendees_email
            attendees_name
            meeting_name
          }
          ... on CalendlyMeeting {
            eventType: event_type {
              color
            }
          }
          attendees {
            kind
            email
            name
            ... on InviteeAttendee {
              cancelled
              uuid
              rsvp
            }
          }
          meeting_intelligence {
            uuid
            status
          }
          passages {
            edges {
              node{
                words
                start_time
                speaker {
                  name
                }
                passage_highlight {
                  best_window_starting_char
                  highlighted_words
                }
                passage_index
              }
            }
            total
          }
        }
      }
      total
    }
  }
`

const GetMeetingsCountDocument = `
  query getMeetings(
    $query: String!,
    $status: MeetingStatus,
    $statuses: [MeetingStatus!],
    $searchIn: [MeetingSearchInFields!],
    $startTimeRange: DateFilter,
    $userUuids: [String!],
    $inviteeEmails: [String!],
    $eventTypeUuids: [String!],
    $packageUuids: [ID!],
    $kinds: [MeetingKind!],
    $meetingUuid: ID,
    $role: [MeetingRole],
    $findBestCharForPassages: Boolean,
    $isOrganizer: Boolean,
    $includeNotetakerIneligibleExternalEvents: Boolean,
  ) {
    meetings_search(
      input: {
        query: $query,
        searchIn: $searchIn,
        findBestCharForPassages: $findBestCharForPassages,
        filter: {
          start_time: $startTimeRange,
          user_uuids: $userUuids,
          invitee_emails: $inviteeEmails,
          event_type_uuids: $eventTypeUuids,
          package_uuids: $packageUuids,
          kinds: $kinds,
          status: $status,
          statuses: $statuses,
          uuid: $meetingUuid,
          role: $role,
          is_organizer: $isOrganizer,
          include_notetaker_ineligible_external_events: $includeNotetakerIneligibleExternalEvents,
        }
      }
    ) {
      total
    }
  }
`

const buildGetMeetingsQuery = (variables: any) => {
  const filterString = buildFilterString(variables)

  // istanbul ignore next
  const query = `
    query getMeetings($first: Int, $before: String, $after: String) {
      meetings(input: {
        pagination: {first: $first, before: $before, after: $after}
        filter: {${filterString}}
        orderBy: {start_time: ${variables.order || 'ASC'}}
      }) {
        total
        edges {
          node {
            uuid
            owner_user_uuid
            kind
            name
            cancelled
            status
            start_time
            end_time
            location
            location_kind
            ... on CalendlyMeeting {
              invitees_limit
              invitees_count
              event_type {
                color
                kind
              }
            }
            meeting_intelligence {
              uuid
              status
              error
            }
            attendees {
              ... on InviteeAttendee {
                cancelled
                uuid
                rsvp
              }
              ... on HostAttendee {
                uuid
              }
              ... on CoHostAttendee {
                uuid
              }
              kind
              name
              email
              timezone
            }
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  `

  return query
}

const buildGetMeetingsFilterCountQuery = (variables: any) => {
  const filterString = buildFilterString(variables)

  // istanbul ignore next
  return `
    query getMeetings {
      meetings(input: {
        filter: {${filterString}}
      }) {
        total
      }
    }
  `
}

const GetMeetingByIdSupergraphDocument = `
  query GetMeeting($id: ID!) {
    getMeetingById(id: $id) {
      uuid
      kind
      id
      name
      cancelled

      start_time
      end_time
      scheduled_at
      show_event_type_time
      event_type_start_time
      event_type_end_time
      event_type_timezone_identifier
      publisher_start_time
      publisher_end_time
      past
      started
      modified

      location
      location_kind
      location_type
      location_additional_info

      external_location {
        join_url
        kind
        name
        phone_number
        show_location_issue
        error_notice
      }

      policy {
        cancel
        update
        reschedule
      }

      internal_notes

      meeting_intelligence {
        summary
        uuid
      }

      cancellation {
        cancelled_at
        cancelled_by
        reason
        attribution
      }
      new_schedulable {
        scheduled_at
        event_time
      }
      old_schedulable {
        created_at
        cancelled_at
        reason
      }

      ... on Meetings_CalendlyMeeting {
        invitees_limit
        filled_count
        event_guest_emails
        modified
        collective_members_uuids
        show_rescheduling_modal

        invitee {
          id
          uuid
          name
          email
          no_show
          tracking_params {
            label
            value
          }
          form_builder_submission {
            questions {
              name
              values
            }
          }
          event_fields {
            name
            values
          }
          charge {
            captured
            amount_with_symbol
            currency
            provider_name
            provider
            amount
            external_charge_link
            booking_code_info {
              code
              booking_code_type
              discount_value_with_symbol
              percent_off
            }
          }
        }

        event_type {
          id
          kind
          color
          name
          edit_path
          type
          deleted
          duration
          booking_method
          pooling_type
          memberships {
            id
            user_uuid
            name
          }
          access_grants {
            user_uuid
          }
        }

        page_name
        is_collective
        page_members {
          uuid
          name
        }
      }
    }
  }
`

/**
 * Shared meetings GraphQL API.
 * This API is used by both the meetings feature and the realtime meetings feature.
 */
export const meetingsGraphqlApi = rootApi
  .enhanceEndpoints({ addTagTypes: MEETINGS_TAG_TYPES })
  .injectEndpoints({
    endpoints: (build: EndpointBuilder<any, any, any>) => ({
      searchMeetings: build.query({
        query: (variables: any) => ({ document: GetMeetingsDocument, variables }),
        providesTags: (result: MeetingsSearchResult | undefined, error: unknown) => {
          const tags = [buildMeetingListTag()]
          if (result?.meetings_search?.edges) {
            result.meetings_search.edges.forEach((edge) => {
              if (edge?.node?.uuid) {
                tags.push(buildMeetingTag(edge.node.uuid))
              }
            })
          }
          return tags
        },
      }),
      getSearchResultsCount: build.query({
        query: (variables: any) => ({ document: GetMeetingsCountDocument, variables }),
        providesTags: [buildMeetingListTag()],
      }),
      getMeetingsFilterCount: build.query({
        query: (variables: any) => {
          const mapped = applyScopeMapping(variables)
          return { document: buildGetMeetingsFilterCountQuery(mapped), variables: mapped }
        },
      }),
      getMeetings: build.query({
        query: (variables: any) => {
          const mapped = applyScopeMapping(variables)
          const newVariables = { ...mapped }
          if (variables.today) {
            newVariables.first = 10_000
          } else {
            newVariables.first = 30
          }
          return { document: buildGetMeetingsQuery(newVariables), variables: newVariables }
        },
        transformResponse: (response: any) => ({
          ...response,
          meetings: {
            ...response.meetings,
            edges: response.meetings.edges
              .filter((edge: any) => edge?.node != null)
              .map((edge: any) => ({ node: { ...edge.node, key: crypto.randomUUID() } }))
          }
        }),
        providesTags: (result: MeetingsGraphQLResult | undefined, error: unknown) => {
          const tags = [buildMeetingListTag()]
          if (result?.meetings?.edges) {
            result.meetings.edges.forEach((edge) => {
              if (edge?.node?.uuid) {
                tags.push(buildMeetingTag(edge.node.uuid))
              }
            })
          }
          return tags
        }
      }),
      getMeeting: build.query({
        query: (variables: any) => {
          // Organizer + notetaker-external flags are fixed in buildFilterString (not from variables).
          const mapped = applyScopeMapping(variables)
          const vars = { ...mapped, first: 1 }
          return {
            document: buildGetMeetingsQuery(vars),
            variables: vars,
          }
        },
        transformResponse: (response: any) => response.meetings.edges[0]?.node || null,
        providesTags: (result: GraphQLMeetingResult | undefined, error: unknown, args: { uuid?: string } | undefined) => {
          const tags = [buildMeetingListTag()]
          if (result?.uuid) {
            tags.push(buildMeetingTag(result.uuid))
          } else if (args?.uuid) {
          // If we have a UUID in args but no result yet, still tag the individual meeting
            tags.push(buildMeetingTag(args.uuid))
          }
          return tags
        },
        // Suppress error alert when used as 404 fallback; meeting detail clears rail without toasting
        extraOptions: { handleErrors: false }
      }),
      getMeetingFromSupergraph: build.query({
        query: ({ id }: { id: string }) => ({
          document: GetMeetingByIdSupergraphDocument,
          variables: { id },
          meta: { baseUrl: getSupergraphBaseUrl(), fetchAuthToken: true },
        }),
        transformResponse: (response: any) => response?.getMeetingById ?? null,
        providesTags: (result: GraphQLMeetingResult | undefined, error: unknown, args: { id?: string } | undefined) => {
          const tags = [buildMeetingListTag()]
          if (result?.uuid) {
            tags.push(buildMeetingTag(result.uuid))
          } else if (args?.id) {
            tags.push(buildMeetingTag(args.id))
          }
          return tags
        },
        extraOptions: { handleErrors: false }
      })
    })
  })

// Export hooks with Gql suffix to distinguish from REST API hooks
export const {
  useSearchMeetingsQuery,
  useLazySearchMeetingsQuery,
  useGetSearchResultsCountQuery,
  useGetMeetingsFilterCountQuery,
  useLazyGetMeetingsFilterCountQuery,
  useGetMeetingsQuery: useGQLGetMeetingsQuery,
  useGetMeetingQuery: useGQLGetMeetingQuery,
  useGetMeetingFromSupergraphQuery,
  useLazyGetMeetingsQuery: useLazyGQLGetMeetingsQuery,
  useLazyGetMeetingQuery: useLazyGQLGetMeetingQuery
} = meetingsGraphqlApi
